import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { type Schemas, run, scopeQuery } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import { exactText, parseExact } from "@/lib/lossless"
import { useCell } from "@/lib/session"
import { isMasked } from "@/workspace/values"
import { type ErpRow, type ShopRow, localDate, parseMoney } from "./match"

export type Obj = Schemas["ObjectResponse"]

/** The flagship workbook's route id. */
export const WORKBOOK_ID = "shopify-erpnext-orders"

/** The object types the order-match workbook reads and writes. */
export const TYPES = {
  shop: "shopify.Order",
  erp: "commerce.SalesOrder",
  decision: "recon.OrderMatchDecision",
  sync: "recon.OrderSync",
  customer: "party.Customer",
} as const

/**
 * The ERPNext order's customer code, a plain property the live source mapping adds beside the
 * counterparty link, so the sheet joins customers in one read rather than one link read per order.
 */
export const ORDER_CUSTOMER = "showcaselive__customer"

const PAGE = 1000
const MAX_PAGES = 10

/**
 * Every authorized record of one type, page by page (at most 10,000): a workbook cell computes over
 * the whole sheet, not a screenful. `truncated` says when the cap cut it short.
 */
export function useAllObjects(scope: Scope, objectType: string, enabled = true) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: ["wb-all", connection.baseUrl, scope.orgId, scope.environmentId, objectType],
    enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: async ({ signal }) => {
      const items: Obj[] = []
      let cursor: string | null = null
      let pages = 0
      do {
        const page = parseExact(
          await run(connection.baseUrl, () =>
            client.GET("/v1/objects/{object_type}", {
              params: { path: { object_type: objectType }, query: { ...scopeQuery(scope), limit: PAGE, ...(cursor ? { cursor } : {}) } },
              parseAs: "text",
              signal,
            }),
          ),
        ) as Schemas["ObjectList"]
        items.push(...page.items)
        cursor = page.nextCursor ?? null
        pages += 1
      } while (cursor && pages < MAX_PAGES)
      return { items, truncated: cursor !== null, readAt: Date.now() }
    },
  })
}

function text(value: unknown): string | null {
  if (typeof value === "string") return value
  return exactText(value) ?? (typeof value === "number" ? String(value) : null)
}

function values(o: Obj): Record<string, unknown> {
  return o.values as Record<string, unknown>
}

export function toShopRow(o: Obj): ShopRow {
  const v = values(o)
  const totalText = text(v.total_price) ?? ""
  const createdAt = text(v.created_at) ?? ""
  const masked = o.masked as Record<string, unknown> | undefined
  return {
    key: o.key,
    name: text(v.order_name) ?? o.key,
    totalText,
    total: parseMoney(totalText),
    currency: text(v.currency) ?? "",
    createdAt,
    date: localDate(createdAt),
    status: text(v.financial_status) ?? "",
    email: isMasked(masked, "customer_email") ? null : text(v.customer_email),
    emailHidden: isMasked(masked, "customer_email"),
    reference: text(v.erp_reference),
    version: o.version,
  }
}

/** The customer's email as the live source mapping adds it (party.Customer has no email of its own). */
export const CUSTOMER_EMAIL = "showcaselive__email"

/** A customer's display name and email by key, each null when hidden for this reader. */
export interface CustomerContact {
  readonly name: string | null
  readonly hidden: boolean
  readonly email: string | null
  readonly emailHidden: boolean
  readonly version: number
}
export type CustomerNames = ReadonlyMap<string, CustomerContact>

export function customerNames(items: readonly Obj[]): CustomerNames {
  return new Map(
    items.map((o) => {
      const masked = o.masked as Record<string, unknown> | undefined
      const hidden = isMasked(masked, "display_name")
      const emailHidden = isMasked(masked, CUSTOMER_EMAIL)
      return [o.key, { name: hidden ? null : text(values(o).display_name), hidden, email: emailHidden ? null : text(values(o)[CUSTOMER_EMAIL]), emailHidden, version: o.version }] as const
    }),
  )
}

/**
 * Customer names and emails for the match's customer signal. A refused or capped read leaves the signal
 * unknown rather than blocking the match; `settled` says the read is no longer in flight.
 */
export function useCustomerNames(scope: Scope, enabled: boolean): { names: CustomerNames; settled: boolean } {
  const read = useAllObjects(scope, TYPES.customer, enabled)
  const items = read.isSuccess && !read.isError && !read.data.truncated ? read.data.items : null
  const names = useMemo(() => customerNames(items ?? []), [items])
  return { names, settled: !enabled || !read.isPending }
}

export function toErpRow(o: Obj, customers: CustomerNames = new Map()): ErpRow {
  const v = values(o)
  const total = text(v.total_amount)
  const masked = o.masked as Record<string, unknown> | undefined
  const customerKey = isMasked(masked, ORDER_CUSTOMER) ? null : text(v[ORDER_CUSTOMER])
  const customer = customerKey ? customers.get(customerKey) : undefined
  return {
    key: o.key,
    number: text(v.order_number) ?? o.key,
    date: text(v.order_date),
    total: total === null ? null : Number(total),
    currency: text(v.currency) ?? "",
    status: text(v.status) ?? "",
    customerKey,
    customer: customer?.name ?? null,
    customerHidden: customer?.hidden ?? isMasked(masked, ORDER_CUSTOMER),
    customerEmail: customer?.email ?? null,
    customerEmailHidden: customer?.emailHidden ?? isMasked(masked, ORDER_CUSTOMER),
    customerVersion: customer?.version ?? 0,
    version: o.version,
  }
}

/** A recorded verdict on one pair, from its decision record. */
export interface Decision {
  readonly id: string
  readonly outcome: "accepted" | "rejected"
  readonly version: number
}

export function toDecision(o: Obj): Decision | null {
  const outcome = text(values(o).outcome)
  return outcome === "accepted" || outcome === "rejected" ? { id: o.key, outcome, version: o.version } : null
}
